import { useState } from "react"
import { Building2, ChevronDown, ChevronRight } from "lucide-react"
import { StatusPill } from "procurement/design"
import { Checkbox } from "../../ui"
import BookThumb from "./BookThumb"
import RowActions from "./RowActions"
import { at, demandRowKey, demandStatus, formatQty, isUnmappedRow, num, rowStock } from "../../bookInventory/model"

const Qty = ({ value, lowerBound = false, shortage = false }) => {
  const tone = shortage && value != null ? (num(value) > 0 ? " bi-gap bi-gap--neg" : " bi-gap bi-gap--zero") : ""
  return (
    <>
      <div className={`bi-qty${tone}`}>
        {lowerBound && value != null ? "≥ " : ""}
        {formatQty(value)}
      </div>
      {value != null && <div className="bi-unit">books</div>}
    </>
  )
}

const groupStatus = (group) =>
  group.shortCount > 0
    ? { tone: "danger", label: `${group.shortCount} ${group.shortCount === 1 ? "item" : "items"} short` }
    : { tone: "success", label: "Enough" }

const NumberCells = ({ values, incomplete }) => (
  <>
    <td className="ds-td-right"><Qty value={values.actualNeeded} lowerBound={incomplete?.has("actualNeeded")} /></td>
    <td className="ds-td-right"><Qty value={values.estimatedNeeded} lowerBound={incomplete?.has("estimatedNeeded")} /></td>
    <td className="ds-td-right"><Qty value={values.stock} lowerBound={incomplete?.has("stock")} /></td>
    <td className="ds-td-right">
      <Qty value={values.actualShortage} lowerBound={incomplete?.has("actualShortage")} shortage />
    </td>
    <td className="ds-td-right">
      <Qty value={values.estimatedShortage} lowerBound={incomplete?.has("estimatedShortage")} shortage />
    </td>
  </>
)

const rowValues = (row, days) => ({
  actualNeeded: at(row, "actualNeeded", days),
  estimatedNeeded: at(row, "estimatedNeeded", days),
  stock: rowStock(row),
  actualShortage: at(row, "actualShortage", days),
  estimatedShortage: at(row, "estimatedShortage", days),
})

const OrStatus = ({ row }) => {
  if (row.orderStatus === "HAS_OR") {
    return (
      <>
        <StatusPill tone="info" label="HAS_OR" />
        <div className="bi-unit">{formatQty(row.orRequestedQty)} books</div>
      </>
    )
  }
  return row.orderStatus ? <StatusPill tone="neutral" label={row.orderStatus} /> : "—"
}

const DemandGroupTable = ({ groups, groupBy, days, selected, onToggleRow, onOpenRow, rowMenu }) => {
  // null = untouched: only the first group starts open.
  const [open, setOpen] = useState(null)
  const isOpen = (key, index) => (open ? open.has(key) : index === 0)
  const toggle = (key, index) =>
    setOpen((prev) => {
      const next = new Set(prev || (groups[0] ? [groups[0].key] : []))
      if (isOpen(key, index)) next.delete(key)
      else next.add(key)
      return next
    })

  return (
    <div className="ds-table-scroller">
      <table className="ds-table bi-table">
        <thead>
          <tr>
            <th>{groupBy === "item" ? "Item / Center" : "Center / Item"}</th>
            <th className="ds-th-right">Actual demand +30%</th>
            <th className="ds-th-right">Estimated demand +30%</th>
            <th className="ds-th-right">Stock</th>
            <th className="ds-th-right">Actual shortage</th>
            <th className="ds-th-right">Estimated shortage</th>
            <th>OR status</th>
            <th>Status</th>
            <th className="bi-col-actions">Actions</th>
          </tr>
        </thead>
        <tbody>
          {groups.map((group, index) => {
            const expanded = isOpen(group.key, index)
            const status = groupStatus(group)
            return [
              <tr key={group.key} className="bi-group-row ds-row-click" onClick={() => toggle(group.key, index)}>
                <td>
                  <span className="bi-cell">
                    {expanded ? <ChevronDown size={16} aria-hidden /> : <ChevronRight size={16} aria-hidden />}
                    {groupBy === "item" ? (
                      <BookThumb key={group.imageUrl || "none"} src={group.imageUrl} />
                    ) : (
                      <span className="bi-group-icon" aria-hidden>
                        <Building2 />
                      </span>
                    )}
                    <span className="bi-group-name">{group.label}</span>
                    <span className="bi-group-count">
                      {group.rows.length} {groupBy === "item" ? "center" : "item"}
                      {group.rows.length === 1 ? "" : "s"}
                    </span>
                  </span>
                </td>
                <NumberCells values={group.totals} incomplete={group.incomplete} />
                <td>
                  <span className="bi-unit">
                    {group.orCount}/{group.rows.length} with OR
                  </span>
                </td>
                <td><StatusPill tone={status.tone} label={status.label} /></td>
                <td className="bi-col-actions">
                  <button
                    type="button"
                    className="pc-btn pc-btn--ghost pc-btn--sm"
                    aria-label={expanded ? `Collapse ${group.label}` : `Expand ${group.label}`}
                    aria-expanded={expanded}
                  >
                    {expanded ? <ChevronDown size={16} aria-hidden /> : <ChevronRight size={16} aria-hidden />}
                  </button>
                </td>
              </tr>,
              ...(expanded
                ? group.rows.map((row) => {
                    const key = demandRowKey(row)
                    const status = demandStatus(row, days)
                    const unmapped = isUnmappedRow(row)
                    const primary = groupBy === "item" ? row.centerName || `Center ${row.centerId}` : row.itemName
                    return (
                      <tr
                        key={key}
                        className={`bi-child-row ds-row-click${selected.has(key) ? " is-selected" : ""}`}
                        onClick={() => onOpenRow(row)}
                      >
                        <td>
                          <span className="bi-cell bi-cell--child">
                            {onToggleRow && (
                              <Checkbox
                                checked={selected.has(key)}
                                aria-label={`Select ${row.itemName || "book"} at ${row.centerName || "center"}`}
                                onChange={() => onToggleRow(row)}
                              />
                            )}
                            {groupBy !== "item" && <BookThumb key={row.imageUrl || "none"} src={row.imageUrl} />}
                            <span style={{ minWidth: 0 }}>
                              <span className="bi-book-name">
                                {unmapped && groupBy !== "item" ? "No book mapping configured" : primary || "—"}
                              </span>
                              {groupBy !== "item" && row.itemCode && <div className="bi-book-code">{row.itemCode}</div>}
                            </span>
                          </span>
                        </td>
                        <NumberCells values={rowValues(row, days)} />
                        <td><OrStatus row={row} /></td>
                        <td><StatusPill tone={status.tone} label={status.label} /></td>
                        <td className="bi-col-actions" onClick={(event) => event.stopPropagation()}>
                          <RowActions items={rowMenu(row)} />
                        </td>
                      </tr>
                    )
                  })
                : []),
            ]
          })}
        </tbody>
      </table>
    </div>
  )
}

export default DemandGroupTable
